import { Box, BoxProps } from '@mui/system';
import * as React from 'react';
import { useMeasure } from 'react-use';
import { makeStyles } from 'tss-react/mui';

import { Loader } from '@/components/progress/Loader/Loader';
import { WithClasses } from '@/styles/types';

interface Dimensions {
  width: number;
  height: number;
}

export function getScaleToFit({
  containerWidth,
  containerHeight,
  contentWidth,
  contentHeight,
}: {
  containerWidth: number;
  containerHeight: number;
  contentWidth: number;
  contentHeight: number;
}): number {
  if (contentWidth <= 0 || contentHeight <= 0) return 1;

  return Math.min(
    containerWidth / contentWidth,
    containerHeight / contentHeight
  );
}

function getRenderedSlideDimensionsPx({
  scaleWidthPx,
  aspectRatio,
}: {
  scaleWidthPx: number;
  aspectRatio: number;
}): Dimensions {
  const heightFromRatioPx = Math.floor(scaleWidthPx / aspectRatio);

  return {
    width: scaleWidthPx,
    height: heightFromRatioPx,
  };
}

type StylesProps = Dimensions & {
  scaleRatio: number;
};

const useStyles = makeStyles<StylesProps>()(
  (_theme, { width, height, scaleRatio }) => ({
    root: {
      position: 'relative',
    },
    container: {
      width: `${width}px`,
      height: `${height}px`,
      transform: `scale(${scaleRatio})`,
      transformOrigin: `left top`,
    },
  })
);

/**
 * 'fixed' — standard behavior: content is rendered at scaleWidthPx and
 *   transform-scaled to fit the container, constrained by aspectRatio.
 * 'intrinsic' — unbounded: content flows at its natural height with no
 *   aspect-ratio constraint or scale transform. Used for export pages that
 *   should take up exactly as much space as they need (e.g. large-format
 *   balance sheet exports).
 * 'fit-content' — content keeps its intrinsic dimensions, then scales down to
 *   fit inside a standard slide frame. Used to present large-format exports.
 */
export type ScalableSlideSizing = 'fit-content' | 'fixed' | 'intrinsic';

export type ScalableSlideProps = React.PropsWithChildren<{
  classes?: WithClasses<typeof useStyles>;
  // this is the ratio that the height will be constrained to relative to the width. e.g. if you want
  // a 16:9 aspect ratio, you'd pass in 16/9
  aspectRatio: number;
  // this is the width of the base slide in pixels. you should build all the slide content relative to
  // this width, and then we'll use `transform: scale` to scale the content up/down to the correct
  // size relative to the available offset parent (container) width.
  scaleWidthPx: number;
  // readyToScale is an optional property that can be used to delay the scaling of the slide
  // which may be useful if the slide needs to be initialized before it can be scaled correctly
  readyToScale?: boolean;
  sizing?: ScalableSlideSizing;
}> &
  Partial<BoxProps>;

export function ScalableSlide({
  children,
  aspectRatio,
  scaleWidthPx,
  readyToScale = true,
  sizing = 'fixed',
  ...props
}: ScalableSlideProps) {
  // we want to trust the wrapping element to provide us with an accurate
  // width, and we then want to calculate the available hight based off of that.
  const [ref, { width: containerWidth }] = useMeasure();
  const [contentRef, { width: contentWidth, height: contentHeight }] =
    useMeasure<HTMLDivElement>();
  const containerHeight = containerWidth / aspectRatio;

  // from there, since we're guaranteed to have the exact same aspect ratio
  // in the actual content container, we can use use static dimensions and
  //  `transform: scale` to scale that content into our root element without
  // needing to worry about the fit issues
  const slideDimensions = getRenderedSlideDimensionsPx({
    aspectRatio,
    scaleWidthPx,
  });
  const containerSlideDimensionRatio = readyToScale
    ? containerWidth / slideDimensions.width
    : 1;

  const { classes } = useStyles(
    {
      ...slideDimensions,
      scaleRatio: containerSlideDimensionRatio,
    },
    { props }
  );

  const isIntrinsic = sizing === 'intrinsic';
  const shouldFitContent = sizing === 'fit-content';

  if (shouldFitContent) {
    // Large-format export slides, such as the full balance sheet, have an
    // intrinsic size that can exceed a standard slide in either direction.
    // Measure that content and scale it on both axes so presentation mode can
    // show the complete export inside its fixed slide frame without clipping.
    const hasMeasuredContent = contentWidth > 0 && contentHeight > 0;
    const scaleRatio = getScaleToFit({
      containerWidth,
      containerHeight,
      contentWidth,
      contentHeight,
    });
    const scaledWidth = contentWidth * scaleRatio;
    const scaledHeight = contentHeight * scaleRatio;

    return (
      <Box
        ref={ref}
        data-name="ScalableSlide"
        data-scalable-slide-sized={containerWidth > 0}
        sx={{
          position: 'relative',
          height: containerHeight,
          overflow: 'hidden',
          pageBreakAfter: 'auto',
          pageBreakBefore: 'always',
        }}
        {...props}
      >
        <Box
          ref={contentRef}
          sx={{
            position: 'absolute',
            width: 'max-content',
            left: Math.max(0, (containerWidth - scaledWidth) / 2),
            top: Math.max(0, (containerHeight - scaledHeight) / 2),
            opacity: hasMeasuredContent ? 1 : 0,
            transform: `scale(${scaleRatio})`,
            transformOrigin: 'left top',
          }}
        >
          {children}
        </Box>
      </Box>
    );
  }

  // Intrinsic mode: content flows at its natural size with no fixed width
  // or scale transform. Used for export pages that should fill the
  // available width and grow vertically as needed.
  if (isIntrinsic) {
    return (
      <Box
        data-name="ScalableSlide"
        data-scalable-slide-sized
        sx={{
          pageBreakAfter: 'auto',
          pageBreakBefore: 'always',
        }}
        {...props}
      >
        {children}
      </Box>
    );
  }

  return (
    <Box
      ref={ref}
      data-name="ScalableSlide"
      data-scalable-slide-sized={containerWidth > 0}
      className={classes.root}
      sx={{
        height: containerHeight,
        pageBreakAfter: 'auto',
        pageBreakBefore: 'always',
      }}
      {...props}
    >
      {!readyToScale && (
        // Overlay the (height-reserved) slide area so the loader centers within
        // the slide rather than collapsing to the top.
        <Loader
          boxProps={{
            position: 'absolute',
            top: 0,
            right: 0,
            bottom: 0,
            left: 0,
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
          }}
          circularProgressProps={{ size: 40 }}
        />
      )}
      <Box
        sx={{
          height: '100%',
          // Don't use visibility: hidden because it causes nodes and edges to render at different times
          // in React Flow, leading to misaligned edge calculations. Instead, use opacity which affects
          // the entire render tree consistently.
          opacity: readyToScale ? 1 : 0,
          pointerEvents: readyToScale ? 'auto' : 'none',
        }}
      >
        <div className={classes.container}>{children}</div>
      </Box>
    </Box>
  );
}
